import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LOCAL_STORAGE_PREFIX } from './useLocalStorage';

// An overview keeps its filters (search fields, column filters, filter popup, page) while the user
// works in it, including the details pages opened from it. Once the user goes to any other page,
// they are cleared, so the overview starts empty next time. The site filter always stays.
const ACTIVE_OVERVIEW_KEY = 'qvend:activeOverview';

// An overview's useLocalStorage scope is the last part of its path, e.g. /dashboard/lists/vmcs -> vmcs.
const OVERVIEW_PATH = /^\/dashboard\/lists\/([^/]+)\/?$/;

// Pages opened from an overview row, and the pages those open in turn.
const DETAILS_PATHS = [
    /^\/dashboard\/details\//,
    /^\/dashboard\/items-linkers\//,
    /^\/dashboard\/lists\/terminal-contracts\/?$/,
];

// The site filter lives in the scope of the sites overview, next to that overview's own filters.
const SITES_SCOPE = 'sites';
const SITE_FILTER_KEYS = ['siteFilter', 'siteTypeFilter', 'siteFilterQuery'];
const SITES_SEARCH_KEY = 'searchDataSites';

function isSiteFilterQuerySet(): boolean {
    try {
        return !!JSON.parse(localStorage.getItem(`${LOCAL_STORAGE_PREFIX}:${SITES_SCOPE}:siteFilterQuery`) || '""');
    } catch {
        return false;
    }
}

// The localStorage keys that make up the site filter.
export function siteFilterStorageKeys(): string[] {
    // With "select all" on the sites overview, its search fields make up the site filter.
    const keys = isSiteFilterQuerySet() ? [...SITE_FILTER_KEYS, SITES_SEARCH_KEY] : SITE_FILTER_KEYS;
    return keys.map((key) => `${LOCAL_STORAGE_PREFIX}:${SITES_SCOPE}:${key}`);
}

function clearOverviewFilters(scope: string): void {
    const prefix = `${LOCAL_STORAGE_PREFIX}:${scope}:`;
    const keep = siteFilterStorageKeys();

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix) && !keep.includes(key)) {
            keysToRemove.push(key);
        }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
}

// Call on every page change with the new path.
export function trackOverviewFilters(pathname: string): void {
    try {
        if (DETAILS_PATHS.some((path) => path.test(pathname))) {
            return;
        }

        const overview = OVERVIEW_PATH.exec(pathname)?.[1] ?? null;
        const active = sessionStorage.getItem(ACTIVE_OVERVIEW_KEY);
        if (active && active !== overview) {
            clearOverviewFilters(active);
        }

        if (overview) {
            sessionStorage.setItem(ACTIVE_OVERVIEW_KEY, overview);
        } else {
            sessionStorage.removeItem(ACTIVE_OVERVIEW_KEY);
        }
    } catch (error) {
        console.log(error);
    }
}

export function useOverviewFilterReset(): void {
    const { pathname } = useLocation();

    useEffect(() => {
        trackOverviewFilters(pathname);
    }, [pathname]);
}
